import { User, CatalogPage, Product } from '@vestiaire/api-sdk-angular';
import {
  UserState,
  BuyerStatus,
  SellerStatus,
  UserCategory,
  ListProductsState,
  ListProduct,
  PageUrlState,
  DataLayerState,
  TrackingSellerRating,
  BooleanString,
  YesNoString,
  UserAccountLevel,
  NonLogged
} from './types';
import { ProductHit, TrackingProductList } from '@app/shared/components/catalog/catalog.component';
import { environment } from '@env/environment';
import * as dayjs from 'dayjs';

export const USER_INITIAL_STATE: UserState = {
  cust_firstname: '',
  user_id: '',
  user_email: '',
  user_accountLevel: NonLogged.Value,
  user_category: NonLogged.Value,
  user_buyerStatus: NonLogged.Value,
  user_sellerStatus: NonLogged.Value,
  user_androidApp: NonLogged.Value,
  user_iphoneApp: NonLogged.Value,
  user_date_inscription: '',
  user_relativedate: '',
  user_seller_rating: NonLogged.Value
};

export function mapUser(user: User): UserState {
  let sDateInscription = '';
  let sDateRelative = '';

  if (user.inscriptionDate) {
    const dDateInscription = dayjs(user.inscriptionDate);
    const dNow = dayjs(new Date());
    sDateInscription = dDateInscription.format('YYYY-MM-DD hh:mm:ss');
    const diffDays = dNow.diff(dDateInscription, 'day');
    sDateRelative = 'relativedate=';
    sDateRelative += diffDays > 30 ? `m${Math.round(diffDays / 30)}` : `d${Math.round(diffDays)}`;
  }

  const userState: UserState = {
    user_id: user.id,
    user_email: user.email || '',
    cust_firstname: user.firstname.toLowerCase(),
    user_accountLevel: user.badges.includes(UserAccountLevel.Premium)
      ? UserAccountLevel.Premium
      : UserAccountLevel.Regular,
    user_category: getUserCategory(user),
    user_buyerStatus: getBuyerStatus(user),
    user_sellerStatus: getSellerStatus(user),
    user_iphoneApp: BooleanString.False,
    user_androidApp: BooleanString.False,
    user_date_inscription: sDateInscription,
    user_relativedate: sDateRelative,
    user_seller_rating: getSellerRating(user)
  };

  if (user.apps && user.apps.length) {
    user.apps.forEach(app => {
      if (app['key'] === 'ios') {
        userState.user_iphoneApp = app['value'] !== '0' ? BooleanString.True : BooleanString.False;
      }

      if (app['key'] === 'android') {
        userState.user_androidApp = app['value'] !== '0' ? BooleanString.True : BooleanString.False;
      }
    });
  }

  return userState;
}

function getUserCategory(user: User): UserCategory {
  const nbSold = user.statistics.productsSold;
  const nbBought = user.statistics.productsBought;

  if (nbSold > 0 && nbBought > 0) {
    return UserCategory.Mixed;
  } else if (nbSold > 0) {
    return UserCategory.Seller;
  } else if (nbBought > 0) {
    return UserCategory.Buyer;
  }

  return UserCategory.Visitor;
}

function getBuyerStatus(user: User): BuyerStatus {
  const nbBought = user.statistics.productsBought;

  if (nbBought >= 10) {
    return BuyerStatus.TopBuyer;
  } else if (nbBought >= 2) {
    return BuyerStatus.RepeatedBuyer;
  } else if (nbBought === 1) {
    return BuyerStatus.FirstBuyer;
  }

  return BuyerStatus.Viewer;
}

function getSellerStatus(user: User): SellerStatus {
  const nbSold = user.statistics.productsSold;
  const nbListed = user.statistics.productsListed;
  if (nbSold >= 10) {
    return SellerStatus.TopSeller;
  } else if (nbSold >= 2) {
    return SellerStatus.RepeatedSeller;
  } else if (nbSold === 1) {
    return SellerStatus.FirstSeller;
  } else if (nbListed >= 1) {
    return SellerStatus.Depositor;
  }

  return SellerStatus.Viewer;
}

function getSellerRating(user: User): TrackingSellerRating {
  if (user && user.sellerRating && user.sellerRating.badge) {
    return TrackingSellerRating[user.sellerRating.badge];
  }
  return null;
}

/**
 * product list Data Layer mapping
 * @param products
 */
export function mapListProducts(products: ProductHit[]): ListProductsState {
  const listProducts: ListProduct[] = [];
  products.forEach(product => {
    const listProduct: ListProduct = {
      list_products_brand: product.brand.name,
      list_products_id: String(product.id),
      list_products_name: product.name,
      list_products_unitprice_ati: String(product.price.cents),
      list_products_unitprice_ati_eur: String(product.priceEur.cents),
      list_products_url_page: product.path,
      list_products_url_picture: environment.imgixPath + product.pictures[0].path
    };
    listProducts.push(listProduct);
  });

  return {
    list_products: listProducts
  };
}

/**
 * Data related to the current product.
 * These variables should be set for product pages.
 *
 * - `product_unitprice_ati` Unit price of the product all tax included (local currency)
 * - `product_unitprice_ati_eur` Unit price of the product all tax included (EUR currency)
 */
export const PRODUCT_INITIAL_STATE: DataLayerState = {
  profilId: '',
  product_id: '',
  product_name: '',
  product_discount_ati: '',
  product_unitprice_ati: '',
  product_discount_ati_eur: '',
  product_unitprice_ati_eur: '',
  product_brand: '',
  product_univers: '',
  product_category: '',
  product_subCategory: ''
};

export function mapProduct(product: Product): DataLayerState {
  const state = {
    ...PRODUCT_INITIAL_STATE,
    product_id: product.id,
    product_name: product.name,
    profilId: product.seller.id,
    owner_seller_rating: getSellerRating(product.seller),
    owner_badge_conformity: product.seller.sellerRating.goals.tags.conformity ? YesNoString.Yes : YesNoString.No,
    owner_badge_shipping: product.seller.sellerRating.goals.tags.shipping ? YesNoString.Yes : YesNoString.No,
    owner_badge_volume: product.seller.sellerRating.goals.tags.volume ? YesNoString.Yes : YesNoString.No
  };

  const currentPrice = product.price.cents / 100;
  const regularPrice = product.regularPrice ? product.regularPrice.cents / 100 : 0;

  if (regularPrice > 0) {
    state['product_unitprice_ati'] = regularPrice.toFixed(2);
    state['product_discount_ati'] = currentPrice.toFixed(2);
  } else {
    state['product_unitprice_ati'] = currentPrice.toFixed(2);
  }

  if (product.brand) {
    state['product_brand'] = product.brand.name.toUpperCase();
  }
  if (product.universe) {
    state['product_univers'] = product.universe.name.toLowerCase();
  }
  if (product.category) {
    state['product_subCategory'] = product.category.name;
  }
  if (product.category.parent) {
    state['product_category'] = product.category.parent.name;
  }

  return state;
}

export function mapCatalogPageUrl(page: CatalogPage): PageUrlState {
  let pageUrl = '/productList';
  page.alternateVersions.forEach(alternate => {
    if (alternate.language === 'en') {
      pageUrl += formatGroupingUrl(alternate.path);
    }
  });

  return {
    pageUrl: pageUrl
  };
}

/**
 * Formats new group URLs like "/g/direct-shipping" for example to old catalog urls format
 * @param url
 */
function formatGroupingUrl(url: string): string {
  return url.replace('/g/', '/');
}

export function mapProductPageUrl(product: Product): PageUrlState {
  let brand, universe, category, subCategory;

  if (product.universe) {
    universe = product.universe.name.toLowerCase();
  }

  if (product.category.parent) {
    category = product.category.parent.name;
  }

  if (product.category) {
    subCategory = product.category.name;
  }

  if (product.brand) {
    brand = product.brand.name.toUpperCase();
  }

  return { pageUrl: `/${universe}/productPage/${category}/${subCategory}/${brand}` };
}

export const PRODUCTLIST_INITIAL_STATE: DataLayerState = {
  product_brand: '',
  product_univers: '',
  product_category: '',
  product_subCategory: ''
};

export function mapCatalogDataLayer(data: TrackingProductList): DataLayerState {
  /**
   * [Hacene] Les tableaux de mappings suivants ne sont pas la solution définitive ! vue l'urgence,
   * je m'en sert pour ne pas bloquer le tracking de la product_list.
   *
   */
  const page = data.page;
  const brandFacet = data.brandFacet;

  const brandMapping = (idBrand: string): string => {
    let brandName = '';
    if (brandFacet) {
      Object.keys(brandFacet).forEach(item => {
        if (item.indexOf('#' + idBrand) !== -1) {
          brandName = item.replace('#' + idBrand, '');
        }
      });
    }

    return brandName.toUpperCase();
  };

  const categoryMapping = {
    '2': 'Clothing',
    '3': 'Shoes',
    '5': 'Bags',
    '6': 'Accessories',
    '63': 'Jewellery',
    '12': 'Clothing',
    '13': 'Shoes',
    '14': 'Accessories',
    '140': 'BAGS',
    '141': 'Bags',
    '7': 'Girls',
    '8': 'Boys',
    '9': 'Baby',
    '10': 'Accessories',
    '11': 'Shoes',
    '805': 'Design & decoration',
    '810': 'Sport & Leisure',
    '811': 'High-tech',
    '812': 'Pet accessories',
    '823': 'Art & culture',
    '852': 'Art & Design'
  };

  const universeMapping = {
    '1': 'Women',
    '2': 'Men',
    '3': 'Kids',
    '4': 'Life & Living'
  };

  const subcategoryMapping = {
    '15': 'Dresses',
    '16': 'Tops',
    '17': 'Skirts',
    '18': 'Trousers',
    '20': 'Coats',
    '21': 'Jackets',
    '23': 'Jeans',
    '25': 'Flat',
    '26': 'Mid heel',
    '27': 'High Heel',
    '30': 'Trainers',
    '31': 'Smart',
    '32': 'Boots',
    '33': 'Sandals',
    '34': 'Jeans',
    '35': 'Trousers',
    '36': 'Shorts',
    '37': 'Knitwear & Sweatshirts',
    '38': 'Shirts',
    '39': 'Jackets ',
    '40': 'Suits',
    '42': 'Sunglasses',
    '43': 'Hats & pull on hats',
    '44': 'Watches',
    '45': 'Small bags, wallets & cases',
    '46': 'Ties',
    '48': 'Bags',
    '49': 'Belts',
    '51': 'Lifestyle',
    '54': 'Belts',
    '55': 'Purses, wallets & cases',
    '56': 'Knitwear',
    '57': 'Clutch bags',
    '58': 'Sunglasses',
    '59': 'Handbags',
    '60': 'Shoulder bags',
    '62': 'Boots',
    '64': 'Trainers',
    '65': 'Dresses',
    '66': 'Trousers',
    '68': 'Tops',
    '69': 'Knitwear',
    '70': 'Jackets & Coats',
    '71': 'Trousers',
    '73': 'Knitwear',
    '74': 'Tops',
    '75': 'Jackets & Coats',
    '76': 'Pyjamas & Bodysuits',
    '77': 'Trousers & Shorts',
    '79': 'Knitwear',
    '80': 'Tops',
    '81': 'Jackets, Coats & Pramsuits',
    '82': 'Boots',
    '84': 'Trainers',
    '85': 'Sandals',
    '86': 'Ballet flats',
    '87': 'Slippers',
    '88': 'Hats & Gloves',
    '89': 'Gloves',
    '90': 'Belts.Suspenders',
    '91': 'Scarves',
    '92': 'Bags & Pencil cases',
    '93': 'Jewellery',
    '94': 'Lifestyle',
    '95': 'Sunglasses',
    '125': 'Scarves',
    '134': 'Skirts',
    '135': 'Scarves & pocket squares',
    '137': 'Dresses',
    '138': 'Lifestyle',
    '139': 'Hats',
    '150': 'Outfits',
    '151': 'Outfits',
    '156': 'Lingerie',
    '164': 'Watches',
    '168': 'Jewellery',
    '411': 'Pyjamas & Bodysuits',
    '412': 'Pyjamas & Bodysuits',
    '413': 'Outfits',
    '414': 'Accessories',
    '415': 'Accessories',
    '501': 'Shorts',
    '502': 'Swimwear',
    '503': 'Gloves',
    '505': 'Flats',
    '506': 'Ballet flats',
    '507': 'Sandals',
    '508': 'Mules & Clogs',
    '509': 'Lace ups',
    '510': 'Heels',
    '511': 'Ankle boots',
    '512': 'Coats ',
    '514': 'Cufflinks',
    '515': 'Shorts',
    '516': 'Shorts',
    '517': 'Lace up boots',
    '518': 'Flats',
    '519': 'First shoes',
    '524': 'Polo shirts',
    '525': 'T-shirts',
    '526': 'Backpacks',
    '527': 'Travel bags',
    '591': 'Rings',
    '592': 'Bracelets',
    '593': 'Pins & brooches',
    '594': 'Necklaces',
    '595': 'Pendants',
    '596': 'Long necklaces',
    '597': 'Jewellery sets',
    '598': 'Earrings',
    '599': 'Wallets',
    '601': 'Hair accessories',
    '602': 'Bag charms',
    '605': 'Lace ups',
    '606': 'Flats',
    '608': 'Gloves',
    '609': 'Silk handkerchief',
    '610': 'Trench coats',
    '611': 'Leather jackets',
    '612': 'Swimwear',
    '797': 'Jumpsuits',
    '806': 'Dinnerware',
    '807': 'Textiles',
    '808': 'Home decor',
    '809': 'Furniture',
    '813': 'Boards',
    '814': 'Cycling',
    '815': 'Golf',
    '816': 'Tennis',
    '817': 'Fitness',
    '818': 'Accessories',
    '819': 'Accessories',
    '820': 'Clothing',
    '821': 'Playground',
    '824': 'Photography',
    '825': 'Fashion',
    '826': 'Art',
    '827': 'Design',
    '828': 'Travel',
    '851': 'Lighting',
    '853': 'Photos',
    '854': 'Paintings',
    '855': 'Artworks',
    '856': 'Posters',
    '865': 'Phone charms',
    '866': 'Swimming',
    '867': 'Sport balls',
    '1051': 'Espadrilles',
    '1052': 'Espadrilles'
  };

  const state = { ...PRODUCTLIST_INITIAL_STATE };

  page.route.parameters.map(param => {
    if (param.name === 'idBrand') {
      state.product_brand = brandMapping(param.value);
    } else if (param.name === 'idUniverse') {
      state.product_univers = universeMapping[param.value];
    } else if (param.name === 'idCategory') {
      state.product_category = categoryMapping[param.value];
    } else if (param.name === 'idSubcategory') {
      state.product_subCategory = subcategoryMapping[param.value];
    }
  });

  return state;
}

export enum TrackingClickPositions {
  InpageBottom = 'inpage bottom'
}
